import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff } from "lucide-react";

export default function PartnerLogin() {
  const { login, stepUpVerify } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Step-up OTP state
  const [otpChallenge, setOtpChallenge] = useState(null); // { challenge_id, otp_sent_to }
  const [otp, setOtp] = useState("");
  const [trustDevice, setTrustDevice] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.step_up) {
        setOtpChallenge({ challenge_id: result.challenge_id, otp_sent_to: result.otp_sent_to });
        setLoading(false);
        return;
      }
      if (!result?.role) {
        setError("Login failed. Please try again.");
        setLoading(false);
        return;
      }
      handleSuccessfulLogin(result);
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await stepUpVerify(otpChallenge.challenge_id, otp, trustDevice);
      handleSuccessfulLogin(result);
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLogin = (user) => {
    if (user.role !== "partner") {
      // This email is registered as a firm user, not a partner account
      setError(
        "This email is not linked to a partner account. " +
        (user.tenant_id
          ? "It's registered as a firm workspace account — use the main Sign In page instead."
          : "Contact your administrator to activate partner portal access.")
      );
      return;
    }
    toast.success(`Welcome back, ${user.name?.split(" ")[0] || "Partner"}!`);
    navigate("/partner", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 partner-portal">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-3">
            {branding.logo_src ? (
              <img src={branding.logo_src} alt={branding.app_name}
                style={{ height: 36, width: "auto", maxWidth: 120 }}
                className="object-contain"
                onError={e => { e.target.style.display = "none"; }} />
            ) : (
              <div className="w-9 h-9 border-2 border-primary rounded-lg flex items-center justify-center shrink-0">
                <span className="font-serif text-primary font-bold text-base">
                  {(branding.app_name || "P")[0]}
                </span>
              </div>
            )}
            <div className="text-left">
              <div className="font-serif text-foreground text-base leading-none font-semibold">{branding.app_name || "InnoAim"}</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-primary mt-0.5">Partner Portal</div>
            </div>
          </div>
          {!otpChallenge ? (
            <>
              <h1 className="font-serif text-2xl text-foreground font-light mt-4">Welcome back</h1>
              <p className="text-muted-foreground text-sm mt-1">Sign in to your partner account</p>
            </>
          ) : (
            <>
              <h1 className="font-serif text-2xl text-foreground font-light mt-4">Verify your identity</h1>
              <p className="text-muted-foreground text-sm mt-1">OTP sent to {otpChallenge.otp_sent_to}</p>
            </>
          )}
        </div>

        {!otpChallenge ? (
          /* ── Login Form ── */
          <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-6 shadow-sm space-y-4" data-testid="partner-login-form">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
                data-testid="partner-login-email"
                className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  required
                  data-testid="partner-login-password"
                  className="w-full bg-background border border-border rounded-xl px-4 py-3 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
                <button type="button" onClick={() => setShowPwd(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-2.5" data-testid="partner-login-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="partner-login-submit"
              className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 transition-colors text-sm mt-2">
              {loading ? "Signing in..." : <>Sign In <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        ) : (
          /* ── OTP Form ── */
          <form onSubmit={handleOtpSubmit} className="bg-card border border-border rounded-2xl p-6 shadow-sm space-y-4" data-testid="partner-otp-form">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1.5">One-Time Password</label>
              <input
                type="text"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                required
                autoFocus
                maxLength={6}
                data-testid="partner-otp-input"
                className="w-full bg-background border border-border rounded-xl px-4 py-3 text-foreground text-center font-mono tracking-[0.5em] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground">
              <input type="checkbox" checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)}
                className="rounded" />
              Trust this device for 30 days
            </label>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-2.5">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading || otp.length < 6} data-testid="partner-otp-submit"
              className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 transition-colors text-sm">
              {loading ? "Verifying..." : <>Verify & Sign In <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button type="button" onClick={() => { setOtpChallenge(null); setOtp(""); }}
              className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1">
              Back to login
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            New partner?{" "}
            <Link to="/partner/register" className="text-primary hover:underline" data-testid="partner-register-link">
              Apply now
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
